import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import Swal from "sweetalert2";
import {
  useCompanySourcesStore,
  type CompanySource,
  type CompanySourceFormData,
} from "../../../../../../store/admin/companySourcesStore";
import { useProvidersStore } from "../../../../../../store/reseller-admin/providersStore";
import { getCustomSelectStyles } from "../../../../shared";
import type { ThemeClasses, Company } from "../types";
import Modal from "../../../../../../components/base/Modal";
import { useIsUserDev } from "../../../../../../hooks/useIsUserDev";

interface CompanySourcesModalProps {
  company: Company;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
}

type ViewMode = "list" | "add" | "edit";

const TYPE_OPTIONS = ["api-poll"];
const IMEIS_OPTIONS = ["discover"];

const initialSourceForm: CompanySourceFormData = {
  active: false,
  type: "api-poll",
  provider_id: "",
  imeis: "discover",
  max_parallel: 8,
  poll_interval_sec: 3600,
  credentials: {},
  credentials_version: "0",
};

export function CompanySourcesModal({
  company,
  themeClasses,
  isDarkMode,
  onClose,
}: CompanySourcesModalProps) {
  const {
    sources,
    loading,
    fetchSources,
    createSource,
    updateSource,
    deleteSource,
    clearSources,
  } = useCompanySourcesStore();

  const providers = useProvidersStore((s) => s.providers);
  const fetchProviders = useProvidersStore((s) => s.fetchProviders);

  const { isUserDev } = useIsUserDev();

  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedSource, setSelectedSource] = useState<CompanySource | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CompanySourceFormData>({ defaultValues: initialSourceForm });

  const active = watch("active");
  const providerId = watch("provider_id");
  const type = watch("type");
  const imeis = watch("imeis");

  const selectStyles = getCustomSelectStyles(isDarkMode, false);
  // Una fuente solo puede usar proveedores cuyo `adapter` coincida con el
  // `origin` de la compañía (mapon o wialon).
  const compatibleProviders = useMemo(
    () =>
      providers.filter(
        (p) => !!p.adapter && (company.origin || []).includes(p.adapter),
      ),
    [providers, company.origin],
  );
  const providerOptions = compatibleProviders.map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const typeOptions = TYPE_OPTIONS.map((v) => ({ value: v, label: v }));
  const imeisOptions = IMEIS_OPTIONS.map((v) => ({ value: v, label: v }));
  const selectedProvider = useMemo(
    () => providers.find((p) => p.id === providerId) || null,
    [providers, providerId],
  );
  const credentialSchema = selectedProvider?.credentials_schema || [];

  useEffect(() => {
    fetchSources(company.id);
    fetchProviders({ limit: 200, page: 1 });
    return () => {
      clearSources();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id]);

  const inputClass = `w-full px-3 py-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50`;
  const labelClass = `block text-sm font-medium ${themeClasses.text} mb-1`;

  const openAdd = () => {
    setSelectedSource(null);
    reset(initialSourceForm);
    setViewMode("add");
  };

  const openEdit = (source: CompanySource) => {
    setSelectedSource(source);
    reset({
      active: source.active ?? false,
      type: source.type || "",
      provider_id: source.provider_id || "",
      imeis: source.imeis || "discover",
      max_parallel: source.max_parallel ?? null,
      poll_interval_sec: source.poll_interval_sec ?? null,
      credentials: source.credentials || {},
      credentials_version: source.credentials_version || "0",
    });
    setViewMode("edit");
  };

  const backToList = () => {
    setViewMode("list");
    setSelectedSource(null);
    reset(initialSourceForm);
  };

  const onSubmit = async (data: CompanySourceFormData) => {
    setIsSubmitting(true);

    const creds: Record<string, string> = {};
    credentialSchema.forEach((f) => {
      creds[f.field] = data.credentials?.[f.field] || "";
    });

    const payload: CompanySourceFormData = {
      active: data.active,
      type: data.type,
      provider_id: data.provider_id,
      imeis: data.imeis,
      max_parallel:
        data.max_parallel === null || Number.isNaN(data.max_parallel)
          ? null
          : Number(data.max_parallel),
      poll_interval_sec:
        data.poll_interval_sec === null || Number.isNaN(data.poll_interval_sec)
          ? null
          : Number(data.poll_interval_sec),
      credentials: creds,
      credentials_version: data.credentials_version || "0",
    };

    const result =
      viewMode === "edit" && selectedSource
        ? await updateSource(company.id, selectedSource.id, payload)
        : await createSource(company.id, payload);

    setIsSubmitting(false);

    if (result.success) {
      await Swal.fire({
        title: "¡Éxito!",
        text:
          viewMode === "edit"
            ? "Fuente actualizada correctamente"
            : "Fuente creada correctamente",
        icon: "success",
        confirmButtonColor: "#16a34a",
      });
      backToList();
    } else {
      await Swal.fire({
        title: "Error",
        text:
          result.error ||
          (viewMode === "edit"
            ? "Error al actualizar la fuente"
            : "Error al crear la fuente"),
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleDelete = async (source: CompanySource) => {
    const providerName =
      providers.find((p) => p.id === source.provider_id)?.name ||
      source.type ||
      source.id;
    const result = await Swal.fire({
      title: "¿Eliminar fuente?",
      text: `¿Realmente desea eliminar la fuente "${providerName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    const res = await deleteSource(company.id, source.id);
    if (res.success) {
      await Swal.fire({
        title: "¡Éxito!",
        text: "Fuente eliminada correctamente",
        icon: "success",
        confirmButtonColor: "#16a34a",
      });
    } else {
      await Swal.fire({
        title: "Error",
        text: res.error || "No se pudo eliminar la fuente",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  // ---- LIST VIEW ----
  const listContent = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          Fuentes de datos de {company.name}
        </p>
        {isUserDev && (
          <button
            onClick={openAdd}
            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-primary-500 text-gray-900 text-sm font-medium hover:bg-primary-400 transition-colors"
          >
            <i className="ri-add-line"></i>
            Nueva Fuente
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
        </div>
      ) : sources.length === 0 ? (
        <div className={`text-center py-12 ${themeClasses.textSecondary}`}>
          <i className="ri-database-2-line text-5xl mb-3 block opacity-50"></i>
          <p className="text-sm">No hay fuentes configuradas para esta compañía</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sources.map((source) => {
            const providerName =
              providers.find((p) => p.id === source.provider_id)?.name ||
              source.provider_id ||
              "—";
            return (
              <div
                key={source.id}
                className={`flex items-center justify-between p-3 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${themeClasses.text}`}>
                      {source.type || "Sin tipo"}
                    </span>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        source.active
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                      }`}
                    >
                      {source.active ? "Activa" : "Inactiva"}
                    </span>
                  </div>
                  <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
                    {providerName}
                    {" · "}intervalo: {source.poll_interval_sec ?? "—"}s
                  </p>
                </div>
                {isUserDev && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openEdit(source)}
                      className="p-2 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-primary-500/20 transition-colors"
                      title="Editar"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(source)}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                      title="Eliminar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ---- FORM VIEW ----
  const formContent = (
    <form id="company-source-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <button
        type="button"
        onClick={backToList}
        className={`flex items-center gap-1 text-sm ${themeClasses.textSecondary} mb-2`}
      >
        <i className="ri-arrow-left-line"></i>
        Volver a la lista
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Provider */}
        <div className="sm:col-span-2">
          <label className={labelClass}>
            Proveedor <span className="text-red-500">*</span>
          </label>
          <Select
            options={providerOptions}
            styles={selectStyles}
            placeholder="Seleccionar proveedor"
            value={providerOptions.find((o) => o.value === providerId) || null}
            onChange={(opt: any) => setValue("provider_id", opt?.value || "", { shouldDirty: true })}
            isSearchable
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          <input
            type="hidden"
            {...register("provider_id", { required: "El proveedor es requerido" })}
          />
          {errors.provider_id && (
            <p className="text-red-500 text-xs mt-1">{errors.provider_id.message}</p>
          )}
          <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
            Solo se muestran proveedores del tipo de la compañía
            {company.origin && company.origin.length > 0
              ? ` (${company.origin.join(", ")})`
              : ""}.
          </p>
          {compatibleProviders.length === 0 && (
            <p className="text-amber-500 text-xs mt-1">
              No hay proveedores compatibles con esta compañía.
            </p>
          )}
        </div>

        {/* Type */}
        <div>
          <label className={labelClass}>
            Tipo <span className="text-red-500">*</span>
          </label>
          <Select
            options={typeOptions}
            styles={selectStyles}
            placeholder="Seleccionar tipo"
            value={typeOptions.find((o) => o.value === type) || null}
            onChange={(opt: any) => setValue("type", opt?.value || "", { shouldDirty: true })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          <input
            type="hidden"
            {...register("type", { required: "El tipo es requerido" })}
          />
          {errors.type && (
            <p className="text-red-500 text-xs mt-1">{errors.type.message}</p>
          )}
        </div>

        {/* Estado */}
        <div>
          <label className={labelClass}>Estado</label>
          <button
            type="button"
            onClick={() => setValue("active", !active, { shouldDirty: true })}
            className={`w-full flex items-center justify-center gap-2 px-3 h-[42px] rounded-xl border text-sm transition-all ${
              active
                ? "bg-green-100 border-green-400 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.textSecondary}`
            }`}
          >
            <i className={`${active ? "ri-toggle-fill" : "ri-toggle-line"} text-base`}></i>
            {active ? "Activa" : "Inactiva"}
          </button>
        </div>

        {/* IMEIS */}
        <div>
          <label className={labelClass}>IMEIS</label>
          <Select
            options={imeisOptions}
            styles={selectStyles}
            placeholder="Seleccionar IMEIS"
            value={imeisOptions.find((o) => o.value === imeis) || null}
            onChange={(opt: any) => setValue("imeis", opt?.value || "", { shouldDirty: true })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        {/* Max parallel */}
        <div>
          <label className={labelClass}>Máx. en paralelo</label>
          <input
            type="number"
            min={0}
            {...register("max_parallel", {
              setValueAs: (v) =>
                v === "" || v === null || v === undefined ? null : Number(v),
            })}
            className={inputClass}
            placeholder="Ej: 8"
          />
        </div>

        {/* Poll interval */}
        <div>
          <label className={labelClass}>Intervalo de sondeo (seg)</label>
          <input
            type="number"
            min={0}
            {...register("poll_interval_sec", {
              setValueAs: (v) =>
                v === "" || v === null || v === undefined ? null : Number(v),
            })}
            className={inputClass}
            placeholder="Ej: 3600"
          />
          <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
            En segundos. 3600 = 1 hora.
          </p>
        </div>

        {/* Credentials version (auto) */}
        <div>
          <label className={labelClass}>Versión de credenciales</label>
          <input
            {...register("credentials_version")}
            className={`${inputClass} opacity-70 cursor-not-allowed`}
            placeholder="Ej: 0"
            readOnly
          />
          <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
            Se incrementa automáticamente al cambiar las credenciales.
          </p>
        </div>
      </div>

      {/* Dynamic provider credentials */}
      <div>
        <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-2 flex items-center gap-1`}>
          <i className="ri-key-2-line"></i> Credenciales del proveedor
        </h3>
        {!selectedProvider ? (
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            Selecciona un proveedor para cargar sus credenciales.
          </p>
        ) : credentialSchema.length === 0 ? (
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            Este proveedor no tiene esquema de credenciales.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {credentialSchema.map((f) => {
              const isVisible = !!visibleSecrets[f.field];
              return (
                <div key={f.field}>
                  <label className={labelClass}>
                    {f.label || f.field}
                    {f.secret && (
                      <i className="ri-lock-line ml-1 text-amber-500" title="Secreto"></i>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={f.secret && !isVisible ? "password" : "text"}
                      autoComplete="off"
                      {...register(`credentials.${f.field}` as const)}
                      className={`${inputClass}${f.secret ? " pr-10" : ""}`}
                      placeholder={f.field}
                    />
                    {f.secret && (
                      <button
                        type="button"
                        onClick={() =>
                          setVisibleSecrets((prev) => ({
                            ...prev,
                            [f.field]: !prev[f.field],
                          }))
                        }
                        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded ${themeClasses.textSecondary} ${themeClasses.buttonHover}`}
                        title={isVisible ? "Ocultar" : "Mostrar"}
                      >
                        <i className={isVisible ? "ri-eye-off-line" : "ri-eye-line"}></i>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </form>
  );

  return (
    <Modal
      width="w-full max-w-3xl"
      themeClasses={themeClasses as never}
      onClose={onClose}
      title={
        viewMode === "list"
          ? "Fuentes de la Compañía"
          : viewMode === "edit"
            ? "Editar Fuente"
            : "Nueva Fuente"
      }
      content={
        <div className={isDarkMode ? "text-white" : ""}>
          {viewMode === "list" ? listContent : formContent}
        </div>
      }
      buttonAction={
        viewMode !== "list" ? (
          <button
            type="submit"
            form="company-source-form"
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isSubmitting
              ? "Guardando..."
              : viewMode === "edit"
                ? "Guardar Cambios"
                : "Crear Fuente"}
          </button>
        ) : undefined
      }
    />
  );
}
